/**
 * Cloudflare Turnstile helper. Shared by the FeedbackHub intake form
 * (CUSTOMER_FEEDBACK_PLAN F-6) and the sign-up form (bot mitigation, auth
 * STANDARD.md §2 — the PreSignUp trigger verifies the token server-side).
 *
 * Loads the Turnstile script lazily — only when a form first requests a token —
 * never on page load. If VITE_TURNSTILE_SITE_KEY is unset (local dev), returns
 * undefined and the caller decides how to proceed.
 */

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileApi {
  render: (
    container: string | HTMLElement,
    options: {
      sitekey: string;
      action?: string;
      size?: "normal" | "compact" | "flexible" | "invisible";
      appearance?: "always" | "execute" | "interaction-only";
      execution?: "render" | "execute";
      callback?: (token: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
      retry?: "auto" | "never";
      theme?: "light" | "dark" | "auto";
    },
  ) => string;
  execute: (widgetId: string) => void;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
  getResponse: (widgetId: string) => string | undefined;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;
let hostEl: HTMLDivElement | null = null;
let widgetId: string | null = null;

/** Public Turnstile site key baked into the build, or undefined if unset. */
export function turnstileSiteKey(): string | undefined {
  const key = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  if (typeof key !== "string") return undefined;
  const trimmed = key.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

const siteKey = turnstileSiteKey;

/** Load the Turnstile api.js (explicit-render mode) once. Resolves when ready. */
export function ensureTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-checklist-turnstile="1"]',
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Turnstile script failed to load")),
        { once: true },
      );
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.dataset.checklistTurnstile = "1";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Turnstile script failed to load"));
    document.head.appendChild(script);
  });

  return scriptPromise;
}

function ensureWidget(key: string): string {
  if (!hostEl) {
    hostEl = document.createElement("div");
    hostEl.setAttribute("aria-hidden", "true");
    hostEl.style.position = "fixed";
    hostEl.style.left = "-9999px";
    hostEl.style.top = "0";
    hostEl.style.width = "1px";
    hostEl.style.height = "1px";
    hostEl.style.overflow = "hidden";
    document.body.appendChild(hostEl);
  }

  const api = window.turnstile;
  if (!api) throw new Error("Turnstile API unavailable");

  if (widgetId) return widgetId;

  widgetId = api.render(hostEl, {
    sitekey: key,
    size: "invisible",
    appearance: "execute",
    execution: "execute",
    retry: "auto",
  });
  return widgetId;
}

/**
 * Returns a Turnstile token, or undefined when no site key is configured.
 * Call from form open / submit paths — never on initial page load.
 */
export async function getCaptchaToken(_action?: string): Promise<string | undefined> {
  const key = siteKey();
  if (!key) return undefined;

  await ensureTurnstileScript();
  const api = window.turnstile;
  if (!api) return undefined;

  const id = ensureWidget(key);

  return new Promise<string>((resolve, reject) => {
    // Re-render with a one-shot callback so execute() yields a fresh token.
    api.remove(id);
    widgetId = api.render(hostEl!, {
      sitekey: key,
      size: "invisible",
      appearance: "execute",
      execution: "execute",
      retry: "never",
      callback: (token) => resolve(token),
      "error-callback": () => reject(new Error("Turnstile challenge failed")),
      "expired-callback": () => reject(new Error("Turnstile token expired")),
    });
    api.execute(widgetId);
  });
}

/** True when a public site key is present in the build. */
export function isTurnstileConfigured(): boolean {
  return Boolean(siteKey());
}
